import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

/*
 *  To restyle ONE table only, pass a theme prop instead of editing this:
 *      <Table theme={{ headBg: 'bg-slate-800', headColor: 'text-slate-200' }} />
 * ═══════════════════════════════════════════════════════════════════════════ */
export const TABLE_THEME = {
  /* ── Card shell ─────────────────────────────────────────────────────────── */
  cardBg:            'bg-white',
  cardBorder:        'border border-gray-200',
  cardRadius:        'rounded-xl',
  cardShadow:        'shadow-sm',

  /* ── Card header (the title strip above the table) ──────────────────────── */
  cardHeadBg:        '',
  cardHeadBorder:    'border-b border-gray-200',
  cardHeadPadding:   'px-5 py-3.5',

  titleSize:         'text-sm',
  titleWeight:       'font-semibold',
  titleColor:        'text-gray-900',

  metaSize:          'text-xs',          // the "12 of 40 records" text
  metaWeight:        '',
  metaColor:         'text-gray-500',

  /* ── Table header row ───────────────────────────────────────────────────── */
  headBg:            'bg-gray-50',
  headBorder:        'border-b border-gray-200',
  headPadding:       'px-4 py-3',
  headSize:          'text-[12px]',
  headWeight:        'font-semibold',
  headColor:         'text-gray-500',    // idle column
  headColorActive:   'text-gray-900',    // the column being sorted on
  headHover:         'hover:text-gray-900',
  headTransition:    'transition-colors',
  sortIconColor:     'text-primary-600',
  sortIconSize:      12,

  /* ── Table body ─────────────────────────────────────────────────────────── */
  bodySize:          'text-[13px]',      // base font size for the whole table
  bodyWeight:        '',
  bodyColor:         '',                 // '' = each column sets its own colour
  cellPadding:       'px-4 py-3',
  cellWrap:          'whitespace-nowrap',  // cells stay on one line by default;
                                           // put wrap: true on a column that
                                           // holds long prose
  rowBorder:         'border-b border-gray-100 last:border-0',
  rowHover:          'hover:bg-gray-50',
  rowTransition:     'transition-colors',

  /* ── Totals footer ──────────────────────────────────────────────────────── */
  footBg:            'bg-gray-50',
  footBorder:        'border-t border-gray-300',
  footPadding:       'px-4 py-3',
  footColor:         'text-gray-900',
  footLabelSize:     'text-[12px]',
  footLabelWeight:   'font-semibold',
  footLabelColor:    'text-gray-500',

  /* ── Toolbar (search / filters / add button) — only shown when used ─────── */
  toolbarBg:         '',
  toolbarBorder:     'border-b border-gray-200',
  toolbarPadding:    'px-4 py-3',
  fieldBase:         'h-9 bg-white border border-gray-200 rounded-lg text-xs px-2.5 outline-none text-gray-700',
  fieldFocus:        'focus:border-primary-500',
  fieldLabel:        'text-[11px] font-medium text-gray-500 mb-1 block',
  searchWidth:       'w-[260px] max-w-full',
  addBtn:            'flex items-center gap-2 h-9 px-4 rounded-lg text-xs font-semibold transition-colors',
  addBtnColor:       'bg-primary-600 text-white hover:bg-primary-700',

  /* ── Pagination — only shown when pageSize is set ───────────────────────── */
  pagerBg:           '',
  pagerBorder:       'border-t border-gray-200',
  pagerPadding:      'px-4 py-3',
  pagerSize:         'text-xs',
  pagerColor:        'text-gray-500',
  pagerBtn:          'h-7 min-w-[28px] px-2 rounded-md text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
  pagerBtnIdle:      'text-gray-600 hover:bg-gray-100',
  pagerBtnActive:    'bg-primary-600 text-white hover:bg-primary-700',

  /* ── Loading / empty states ─────────────────────────────────────────────── */
  statePadding:      'py-20',
  stateSize:         'text-sm',
  stateColor:        'text-gray-400',

  /* ── Numbers (the Num helper) ───────────────────────────────────────────── */
  numZero:           'text-gray-300',    // zeros stay visible but recessive
  numNormal:         'text-gray-600',
  numStrong:         'font-semibold text-gray-900',
  emptyDash:         'text-gray-300',    // the — shown for an empty cell

  /* ── Status pill (the StatusPill helper) ────────────────────────────────── */
  pillPadding:       'px-2 py-0.5',
  pillRadius:        'rounded-full',
  pillSize:          'text-[11px]',
  pillWeight:        'font-semibold',
  pillGap:           'gap-1.5',
  pillDotSize:       'w-1.5 h-1.5',
  pillTones: {
    success: { pill: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
    neutral: { pill: 'bg-gray-100 text-gray-500',      dot: 'bg-gray-400'    },
    warning: { pill: 'bg-amber-50 text-amber-700',     dot: 'bg-amber-500'   },
    danger:  { pill: 'bg-red-50 text-red-700',         dot: 'bg-red-500'     },
    info:    { pill: 'bg-blue-50 text-blue-700',       dot: 'bg-blue-500'    },
  },

  /* ── Layout ─────────────────────────────────────────────────────────────── */
  scroll:            'max-h-[calc(100vh-380px)] min-h-[240px]',
};

/* ── A few ready-made looks. Swap one in by spreading it into TABLE_THEME
      above, or pass it as the theme prop: theme={TABLE_PRESETS.darkHeader} ── */
export const TABLE_PRESETS = {
  darkHeader: {
    headBg: 'bg-gray-900',
    headBorder: 'border-b border-gray-900',
    headColor: 'text-gray-400',
    headColorActive: 'text-white',
    headHover: 'hover:text-white',
  },
  brandHeader: {
    headBg: 'bg-primary-50',
    headBorder: 'border-b border-primary-100',
    headColor: 'text-primary-700',
    headColorActive: 'text-primary-900',
    headHover: 'hover:text-primary-900',
  },
  compact: {
    bodySize: 'text-xs',
    headSize: 'text-[11px]',
    cellPadding: 'px-3 py-2',
    headPadding: 'px-3 py-2',
    footPadding: 'px-3 py-2',
  },
  roomy: {
    bodySize: 'text-sm',
    cellPadding: 'px-5 py-4',
    headPadding: 'px-5 py-4',
    footPadding: 'px-5 py-4',
  },
};

/* ═══════════════════════════════════════════════════════════════════════════
 *  Nothing below needs editing to restyle the table — it all reads the theme.
 * ═══════════════════════════════════════════════════════════════════════════ */

/** Join class strings, dropping the empty ones. */
const cx = (...parts) => parts.filter(Boolean).join(' ');

export const alignCls = (a) =>
  a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left';

/* ── cell helpers ────────────────────────────────────────────────────────── */

/**
 * Num — a figure. Zero stays visible but recessive so real numbers carry the
 * eye. `strong` marks the figure the column is actually about.
 */
export function Num({ value, decimals = 2, strong = false, prefix = '', suffix = '', theme }) {
  const t = { ...TABLE_THEME, ...theme };
  const n = Number(value) || 0;
  if (n === 0) return <span className={t.numZero}>{(0).toFixed(decimals)}</span>;
  return (
    <span className={strong ? t.numStrong : t.numNormal}>
      {prefix}{n.toFixed(decimals)}{suffix}
    </span>
  );
}

/** Dash — the empty placeholder. Never render a blank cell. */
export const Dash = ({ theme }) => (
  <span className={{ ...TABLE_THEME, ...theme }.emptyDash}>—</span>
);

/**
 * statusTone — maps the status words used across the project onto a pill tone,
 * so "Active", "Pending", "Overdue" etc. look the same on every screen.
 * Anything unlisted falls back to neutral, so a new status never looks broken.
 */
const STATUS_TONES = {
  active: 'success', approved: 'success', completed: 'success', confirmed: 'success', authorized: 'success', current: 'success', 'paid': 'success',
  posted: 'success', paid: 'success', generated: 'success', selected: 'success',
  sent: 'success', emailed: 'success', downloaded: 'success', released: 'success',
  yes: 'success', open: 'info', 'in progress': 'info', scheduled: 'info',
  pending: 'warning', draft: 'warning', hold: 'warning', incomplete: 'warning', 'not paid': 'warning',
  overdue: 'danger', rejected: 'danger', failed: 'danger', cancelled: 'danger',
  inactive: 'neutral', closed: 'neutral', 'not sent': 'neutral',
  'not selected': 'neutral', no: 'neutral',
};
export const statusTone = (status) =>
  STATUS_TONES[String(status ?? '').trim().toLowerCase()] || 'neutral';

/** StatusPill — tone is a key of TABLE_THEME.pillTones. */
export function StatusPill({ label, tone, dot = true, theme }) {
  const t = { ...TABLE_THEME, ...theme };
  const c = t.pillTones[tone || statusTone(label)] || t.pillTones.neutral;
  return (
    <span
      className={cx('inline-flex items-center', t.pillGap, t.pillPadding, t.pillRadius,
        t.pillSize, t.pillWeight, c.pill)}
    >
      {dot && <span className={cx(t.pillDotSize, 'rounded-full', c.dot)} />}
      {label}
    </span>
  );
}

/**
 * TableTotalsRow — the sticky totals footer.
 *
 *   <TableTotalsRow
 *     label={`Total · ${rows.length} records`}
 *     labelSpan={7}
 *     cells={[
 *       { content: totals.normal.toFixed(2), align: 'right', className: 'text-gray-600' },
 *       { content: totals.ot.toFixed(2),     align: 'right', className: 'font-semibold' },
 *       { content: '', span: 2 },   // trailing spacer
 *     ]}
 *   />
 */
export function TableTotalsRow({ label, labelSpan = 1, cells = [], theme }) {
  const t = { ...TABLE_THEME, ...theme };
  return (
    <tr className={cx(t.footBg, t.footBorder, t.footColor)}>
      {label !== undefined && (
        <td className={cx(t.footPadding, t.footLabelSize, t.footLabelWeight, t.footLabelColor)}
            colSpan={labelSpan}>
          {label}
        </td>
      )}
      {cells.map((c, i) => (
        <td
          key={i}
          colSpan={c.span > 1 ? c.span : undefined}
          className={cx(
            t.footPadding,
            c.content === '' || c.content === undefined ? '' : 'tabular-nums',
            c.align && alignCls(c.align),
            c.className
          )}
        >
          {c.content}
        </td>
      ))}
    </tr>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 *  Table — the common table for the whole project.
 *
 *  Design lifted from the Overtime Details table. This file is DESIGN ONLY —
 *  every page passes its own columns and rows. components/DataTable/DataTable.js
 *  is untouched; new and refactored pages call this instead.
 *
 *  Column shape
 *    {
 *      key:           'employeeId',          // required, unique
 *      label:         'Employee',
 *      align:         'left|center|right',   // default 'left'
 *      width:         '92px',                // fed to <colgroup>
 *      wrap:          true,                  // let this column's text wrap;
 *                                            // cells are single-line by default.
 *                                            // Pair it with a min-w-[..] class,
 *                                            // or beside a grow column it
 *                                            // squeezes to its narrowest line.
 *      grow:          true,                  // this column takes the leftover
 *                                            // width; the rest shrink to fit.
 *                                            // Mark ONE column per table —
 *                                            // usually the name/description —
 *                                            // and leave widths off the others,
 *                                            // otherwise the slack lands in
 *                                            // whichever column has no width
 *                                            // and opens a dead gap.
 *      sortable:      true,                  // default true
 *      hideWhenEmpty: 'otAmount',            // drop column if empty in every row
 *      render:        (row, i) => ...,        // custom cell content
 *      className:     'text-gray-500',        // string, or (row, i) => string
 *      headClassName: '',
 *    }
 * ═══════════════════════════════════════════════════════════════════════════ */
export default function Table({
  columns = [],
  rows = [],
  rowKey = 'id',

  /* restyle just this table — merged over TABLE_THEME */
  theme,

  /* card header */
  title,
  meta,                                   // defaults to the record count
  headerExtra,
  showHeader = true,

  /* toolbar — all optional, nothing renders unless used */
  search = false,                         // true turns the search box on
  searchKeys = [],                        // row fields the search box looks at
  searchPlaceholder = 'Search...',
  searchLabel,                            // label above the box, for alignment
                                          // with other labelled toolbar fields
  toolbarExtra,                           // filters/selects to sit beside search
  toolbarBeforeSearch,
  onAdd,                                  // { label, onClick, icon }

  /* pagination — 0 keeps the table scrolling instead of paging */
  pageSize = 0,

  /* state */
  loading = false,
  loadingMessage = 'Loading...',
  emptyMessage = 'No records found',

  /* sorting — omit sort/onSortChange and the table sorts itself */
  sortable = true,
  defaultSort = null,                     // { key, dir: 'asc' | 'desc' }
  sort: sortProp,
  onSortChange,

  /* layout */
  minWidth = 0,
  scrollClassName,                        // overrides theme.scroll
  maxHeight,                              // one-off inline override
  minHeight,
  stickyHeader = true,

  /* extras */
  emptyValue,                             // what an empty cell shows
  footer,                                 // <TableTotalsRow /> or any <tr>
  onRowClick,
  rowClassName,                           // (row, i) => string
  className = '',
}) {
  const t = useMemo(() => ({ ...TABLE_THEME, ...theme }), [theme]);

  const [innerSort, setInnerSort] = useState(defaultSort);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const isControlled = sortProp !== undefined;
  const sort = isControlled ? sortProp : innerSort;

  const showToolbar = search || toolbarExtra || onAdd;

  /* Search runs before sorting and paging. Keys default to every column. */
  const searchedRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    const keys = searchKeys.length ? searchKeys : columns.map((c) => c.key);
    return rows.filter((r) =>
      keys.some((k) => String(r[k] ?? '').toLowerCase().includes(q))
    );
  }, [rows, query, searchKeys, columns]);

  const toggleSort = (key) => {
    const next = sort && sort.key === key
      ? { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' };
    if (!isControlled) setInnerSort(next);
    onSortChange?.(next);
  };

  /* A column empty in every row is dead space — leave it out. */
  const visibleColumns = useMemo(
    () => columns.filter((c) => {
      if (!c.hideWhenEmpty) return true;
      return searchedRows.some((r) => {
        const v = r[c.hideWhenEmpty];
        return v !== null && v !== undefined && v !== '';
      });
    }),
    [columns, searchedRows]
  );

  /* Only sort internally when the page hasn't sorted for us. */
  const sortedRows = useMemo(() => {
    if (isControlled || !sort?.key) return searchedRows;
    const list = [...searchedRows];
    const mult = sort.dir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mult;
      if (typeof av === 'boolean' || typeof bv === 'boolean')
        return (av === bv ? 0 : av ? 1 : -1) * mult;
      return String(av ?? '').localeCompare(String(bv ?? '')) * mult;
    });
    return list;
  }, [searchedRows, sort, isControlled]);

  /* Paging. pageSize = 0 means the table scrolls instead, as OT Details does. */
  const pageCount = pageSize ? Math.max(1, Math.ceil(sortedRows.length / pageSize)) : 1;
  const currentPage = Math.min(page, pageCount);
  const displayRows = pageSize
    ? sortedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : sortedRows;

  const firstShown = sortedRows.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastShown = Math.min(currentPage * pageSize, sortedRows.length);

  const blank = emptyValue !== undefined ? emptyValue : <Dash theme={theme} />;
  const stateCls = cx(t.statePadding, 'text-center', t.stateSize, t.stateColor);
  const defaultMeta =
    sortedRows.length === 1 ? '1 record' : `${sortedRows.length} records`;
  const metaNode = <span className={cx(t.metaSize, t.metaWeight, t.metaColor)}>{meta ?? defaultMeta}</span>;

  return (
    <div className={cx(t.cardBg, t.cardBorder, t.cardRadius, t.cardShadow, 'overflow-hidden', className)}>
      {showHeader && (title || meta || headerExtra) && (
        <div className={cx(t.cardHeadPadding, t.cardHeadBg, t.cardHeadBorder,
          'flex items-center justify-between')}>
          <h3 className={cx(t.titleSize, t.titleWeight, t.titleColor)}>{title}</h3>
          {headerExtra
            ? <div className="flex items-center gap-3">{headerExtra}{metaNode}</div>
            : metaNode}
        </div>
      )}

      {/* items-end keeps every control sitting on the same bottom line,
          whether or not it has a label above it. */}
      {showToolbar && (
        <div className={cx(t.toolbarPadding, t.toolbarBg, t.toolbarBorder,
          'flex flex-wrap items-end justify-between gap-3')}>
          <div className="flex flex-wrap items-end gap-3 flex-1">
            {toolbarBeforeSearch}

            {search && (
              <div className={t.searchWidth}>
                {searchLabel && <span className={t.fieldLabel}>{searchLabel}</span>}
                <input
                  type="text"
                  value={query}
                  placeholder={searchPlaceholder}
                  onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                  className={cx(t.fieldBase, t.fieldFocus, 'w-full')}
                />
              </div>
            )}
            {toolbarExtra}
          </div>
          {onAdd && (
            <button onClick={onAdd.onClick} className={cx(t.addBtn, t.addBtnColor)}>
              {onAdd.icon ?? <span className="text-sm leading-none">+</span>}
              {onAdd.label}
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className={stateCls}>{loadingMessage}</div>
      ) : sortedRows.length === 0 ? (
        <div className={stateCls}>{emptyMessage}</div>
      ) : (
        <div
          className={cx('overflow-auto', scrollClassName ?? t.scroll)}
          style={maxHeight || minHeight ? { maxHeight, minHeight } : undefined}
        >
          <table
            className={cx('w-full', t.bodySize, t.bodyWeight, t.bodyColor)}
            style={minWidth ? { minWidth } : undefined}
          >
            <colgroup>
              {visibleColumns.map((c) => (
                <col
                  key={c.key}
                  style={
                    /* grow: this column soaks up the leftover width so every
                       other column shrinks to its content — no dead gaps. */
                    c.grow ? { width: '100%' } : c.width ? { width: c.width } : undefined
                  }
                />
              ))}
            </colgroup>

            <thead className={stickyHeader ? 'sticky top-0 z-10' : ''}>
              <tr className={cx(t.headBg, t.headBorder)}>
                {visibleColumns.map((c) => {
                  const canSort = sortable && c.sortable !== false;
                  const active = sort?.key === c.key;
                  return (
                    <th
                      key={c.key}
                      onClick={canSort ? () => toggleSort(c.key) : undefined}
                      title={canSort ? `Sort by ${c.label}` : undefined}
                      className={cx(
                        t.headPadding, t.headSize, t.headWeight, 'whitespace-nowrap',
                        canSort && cx('cursor-pointer select-none', t.headTransition, t.headHover),
                        alignCls(c.align),
                        active ? t.headColorActive : t.headColor,
                        c.headClassName
                      )}
                    >
                      <span className={cx('inline-flex items-center gap-1',
                        c.align === 'right' && 'flex-row-reverse')}>
                        {c.label}
                        {active && (sort.dir === 'asc'
                          ? <ChevronUp size={t.sortIconSize} className={t.sortIconColor} />
                          : <ChevronDown size={t.sortIconSize} className={t.sortIconColor} />)}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {displayRows.map((row, index) => (
                <tr
                  key={row[rowKey] ?? index}
                  onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                  className={cx(
                    t.rowBorder, t.rowHover, t.rowTransition,
                    onRowClick && 'cursor-pointer',
                    rowClassName && rowClassName(row, index)
                  )}
                >
                  {visibleColumns.map((c) => {
                    const value = c.render ? c.render(row, index) : row[c.key];
                    /* className may be a string or (row, i) => string, so a cell
                       can colour itself from its own value. */
                    const extra = typeof c.className === 'function'
                      ? c.className(row, index)
                      : c.className;
                    return (
                      <td
                        key={c.key}
                        className={cx(
                          t.cellPadding,
                          !c.wrap && t.cellWrap,
                          alignCls(c.align),
                          c.align === 'right' && 'tabular-nums',
                          extra
                        )}
                      >
                        {value === null || value === undefined || value === '' ? blank : value}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>

            {footer && <tfoot className="sticky bottom-0">{footer}</tfoot>}
          </table>
        </div>
      )}

      {pageSize > 0 && !loading && sortedRows.length > 0 && (
        <div className={cx(t.pagerPadding, t.pagerBg, t.pagerBorder,
          'flex flex-wrap items-center justify-between gap-3')}>
          <span className={cx(t.pagerSize, t.pagerColor)}>
            Showing {firstShown} to {lastShown} of {sortedRows.length} entries
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={cx(t.pagerBtn, t.pagerBtnIdle)}
            >
              Prev
            </button>
            {pageWindow(currentPage, pageCount).map((p, i) =>
              p === '…' ? (
                <span key={`gap-${i}`} className={cx(t.pagerSize, t.pagerColor, 'px-1')}>…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={cx(t.pagerBtn, p === currentPage ? t.pagerBtnActive : t.pagerBtnIdle)}
                >
                  {p}
                </button>
              )
            )}
            <button
              onClick={() => setPage(currentPage + 1)}
              disabled={currentPage === pageCount}
              className={cx(t.pagerBtn, t.pagerBtnIdle)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Page numbers with an ellipsis once there are more than 7 pages. */
function pageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '…', total];
  if (current >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '…', current - 1, current, current + 1, '…', total];
}